import {
  COMPARISON_TYPES,
  DEVIATION_TYPES,
  NOTIFICATION_TYPES,
} from 'api/data/constants';
import { MULTISELECT_OPERATORS } from 'components/Form/FormikFiltersSection/helpers';
import { TFilter } from 'components/Form/FormikFiltersSection/types';
import { DELIMITER } from 'utils/filters';
import { TObject } from 'utils/types';

import {
  INotification,
  INotificationOutputChannel,
  INotificationOutputChannelsData,
  IOutputChannel,
  ITemplate,
  ITemplateData,
  TNotificationsData,
  TScheduling,
} from './types';

export const normalizeChannel = (data: TObject): IOutputChannel => {
  return {
    createdDate: (data.createdDate as string) || '',
    email: (data.email as string) || '',
    id: (data.id as string) || '',
    name: (data.name as string) || '',
    slackWebhookUrl: (data.slackWebhookUrl as string) || '',
    telegramChannelId: (data.telegramChannelId as string) || '',
    type: (data.type as string) || '',
    updatedDate: (data.updatedDate as string) || '',
    verified: !!data.verified,
  };
};

export const normalizeNotificationChannel = (
  data: INotificationOutputChannelsData,
): INotificationOutputChannel => {
  const { user, ...others } = data;

  return {
    ...normalizeChannel(others),
    user: {
      id: (user.id as string) || '',
      login: (user.login as string) || '',
    },
  };
};

export const normalizeFilter = (data: TObject): TFilter => {
  const value = MULTISELECT_OPERATORS.some((operator) => operator === data?.operation)
    ? (data?.value as string)?.split(DELIMITER).map((item) => Number(item) || item)
    : Number(data?.value) || (data?.value as string);

  return {
    fieldName: (data?.fieldName as string) || null,
    operation: (data?.operation as string) || null,
    value: value || null,
  };
};

const normalizeSchedule = (data: TObject): TScheduling => {
  return {
    dayOfWeek: (data?.dayOfWeek as string) || null,
    endTime: (data?.endTime as string) || null,
    startTime: (data?.startTime as string) || null,
  };
};

export const normalizeNotification = (data: TNotificationsData): INotification => {
  return {
    alertLevel: (data?.alertLevel as string) || null,
    allowedDeviation: data?.allowedDeviation
      ? ((data.allowedDeviation * 100) as number)
      : null,
    baselineApprovalRate: data?.baselineApprovalRate
      ? ((data.baselineApprovalRate * 100) as number)
      : null,
    baselinePeriodSeconds: data?.baselinePeriodSeconds
      ? ((data.baselinePeriodSeconds * 1000) as number)
      : null,
    channels: data?.channels.map(normalizeNotificationChannel),
    comparedGroup: (data?.comparedGroup as string[]) || [],
    comparedToGroup: (data?.comparedToGroup as string[]) || [],
    comparisonType: (data?.comparisonType as COMPARISON_TYPES) || null,
    createdDate: (data?.createdDate as string) || null,
    deviationType: (data?.deviationType as DEVIATION_TYPES) || null,
    enabled: !!data?.enabled,
    evaluationFrequency: (data?.evaluationFrequency as string) || null,
    evaluationPeriodSeconds: data?.evaluationPeriodSeconds
      ? ((data.evaluationPeriodSeconds * 1000) as number)
      : null,
    filters: data?.filters.map(normalizeFilter),
    id: (data?.id as number) || 0,
    minTransactionThreshold: (data?.minTransactionThreshold as number) || null,
    name: (data?.name as string) || null,
    scheduling: data?.scheduling.map(normalizeSchedule),
    schedulingEnabled: !!data?.schedulingEnabled,
    stuckPeriodSeconds: data?.stuckPeriodSeconds
      ? ((data.stuckPeriodSeconds * 1000) as number)
      : null,
    template: {
      id: (data?.template?.id as number) || null,
      name: (data?.template?.name as string) || null,
    },
    type: (data?.type as NOTIFICATION_TYPES) || null,
    updatedDate: (data?.updatedDate as string) || null,
    user: {
      id: (data.user.id as string) || '',
      login: (data.user.login as string) || '',
    },
  };
};

export const normalizeTemplate = (data: ITemplateData): ITemplate => {
  return {
    createdDate: (data.createdDate as string) || '',
    id: (data.id as string) || '',
    name: (data.name as string) || '',
    notificationType: (data.notificationType as string) || '',
    template: (data.template as string) || '',
    updatedDate: (data.updatedDate as string) || '',
    user: {
      id: (data.user.id as string) || '',
      login: (data.user.login as string) || '',
    },
  };
};
